import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Send, Loader, Instagram, Facebook } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDateTime } from '../../utils/formatDate';

const CHANNEL_CONFIG = {
  instagram: { label: 'Instagram', icon: Instagram, iconColor: 'text-[#E1306C]' },
  messenger: { label: 'Facebook', icon: Facebook, iconColor: 'text-[#1877F2]' },
};

// Mirrors LeadWhatsAppPanel.jsx's layout/behavior, generalized to any
// channel with a shared Conversation/InboxMessage history (Instagram,
// Facebook Messenger) instead of WhatsApp's phone-suffix-matched MetaMessage
// lookup — backed by the new /crm/leads/:id/social-messages(+/social-reply)
// endpoints, which resolve the right Conversation for this lead server-side.
export default function LeadSocialChatPanel({ leadId, channel }) {
  const cfg = CHANNEL_CONFIG[channel];
  const Icon = cfg.icon;
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(undefined); // undefined = not loaded yet, null = none found
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/crm/leads/${leadId}/social-messages`, { params: { channel } });
      setMessages(data.messages || []);
      setConversationId(data.conversationId ?? null);
    } catch (err) {
      alertService.error('Error', `Failed to load ${cfg.label} history`);
    } finally {
      setLoading(false);
    }
  }, [leadId, channel, cfg.label]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSend = async () => {
    if (!text.trim() || !conversationId) return;
    setSending(true);
    try {
      await api.post(`/crm/leads/${leadId}/social-reply`, { channel, text: text.trim() });
      setText('');
      await fetchMessages();
    } catch (err) {
      alertService.error('Send Failed', err.response?.data?.message || `Could not send ${cfg.label} message`);
    } finally {
      setSending(false);
    }
  };

  if (loading && conversationId === undefined) {
    return (
      <div className="py-24 text-center h-full bg-white flex items-center justify-center">
        <Loader size={24} className="text-[#0176D3] animate-spin" />
      </div>
    );
  }

  if (conversationId === null) {
    return (
      <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
        <Icon size={48} className="mx-auto mb-4 opacity-10" />
        <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">
          No {cfg.label} Conversation Linked
        </p>
        <p className="text-[11px] text-[#706E6B] mt-2 px-6">
          This lead hasn't messaged on {cfg.label} yet, or the conversation hasn't been linked to this lead.
        </p>
      </div>
    );
  }

  return (
    <div className="sf-card bg-white flex flex-col h-[560px] animate-in fade-in">
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {messages.length > 0 ? (
          messages.map((m) => (
            <div key={m._id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[70%] rounded px-4 py-2.5 text-[13px] font-medium ${
                  m.direction === 'outbound'
                    ? 'bg-[#0176D3] text-white'
                    : 'bg-[#F3F2F2] text-[#080707] border border-[#D8DDE6]'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-white/70' : 'text-[#706E6B]'}`}>
                  {formatDateTime(m.createdAt)}
                  {m.direction === 'outbound' ? ` · ${m.status}` : ''}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-40">
            <MessageSquare size={40} className="mb-3 opacity-20" />
            <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No {cfg.label} Messages Yet</p>
          </div>
        )}
      </div>

      <div className="border-t border-[#D8DDE6] p-4 flex items-end gap-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={`Message via ${cfg.label}...`}
          rows={2}
          className="flex-1 px-3 py-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all resize-none focus:ring-1 focus:ring-[#0176D3]"
        />
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#0176D3] text-white rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] hover:bg-[#014486] disabled:opacity-40 transition-all"
        >
          {sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
